import { setBookingStartingPoint } from '@/helpers';
import { useReducer, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { getSuggestionsFromSearchValue } from './KeywordAutoSuggest.helpers';
import {
  KeywordAutoSuggestAction,
  KeywordAutoSuggestManagerProps,
  KeywordAutoSuggestState,
} from './KeywordAutoSuggest.type';

const keywordAutoSuggestReducer = (
  state: KeywordAutoSuggestState,
  action: KeywordAutoSuggestAction,
): KeywordAutoSuggestState => {
  switch (action.type) {
    case 'ON_INPUT_CHANGE': {
      return { ...state, keyword: action.payload };
    }

    case 'ON_SUGGESTION_LOADED': {
      return { ...state, ...action.payload };
    }

    case 'SET_SHOW_SUGGESTIONS': {
      return { ...state, showSuggestions: action.payload };
    }
    default:
      return state;
  }
};

const useKeywordAutoSuggestManager = ({
  initialState,
  onSelectKeywordSuggestion,
  isSistaminutenSearch,
}: KeywordAutoSuggestManagerProps) => {
  const [state, dispatch] = useReducer(keywordAutoSuggestReducer, initialState);
  const history = useHistory();
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingValueRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const handleOnChange = (_, { newValue: payload = '' }) => {
    dispatch({ type: 'ON_INPUT_CHANGE', payload });
  };

  const handleOnFocus = () => {
    dispatch({ type: 'SET_SHOW_SUGGESTIONS', payload: true });
  };

  const handleOnBlur = (_, { highlightedSuggestion }) => {
    /**
     * if the input is blurred, make sure to abort any ongoing fetch requests
     */
    abortControllerRef.current.abort();
  };

  const handleOnKeyDown = (event: React.KeyboardEvent) => {};

  const handleOnSuggestionsFetchRequested = async ({ value, reason }) => {
    if (reason !== 'input-focused' && reason !== 'input-changed') {
      return;
    }

    pendingValueRef.current = value;

    if (isFetchingRef.current) {
      return;
    }

    /**
     * When fetching keyword suggestions only one request is active at a time. If the user types during an ongoing request,
     * the latest input is saved in `pendingValueRef` and will be fetched after the current request finishes.
     *
     *  Suggestions are cached in localStorage to avoid sending duplicate requests for previously seen inputs.
     *
     * Example flow when user types "tractor":
     * - User types "tra" → triggers request #1
     * - Then types "trac" quickly → triggers request #2 (queued if #1 is still running)
     * - While request #2 is pending, user finishes typing "tractor" → input is saved (not immediately sent)
     * - Once request #2 finishes, request #3 is triggered with "tractor"
     * - Only the latest result ("tractor") is shown in the UI
     */
    const fetchSuggestions = async (input: string) => {
      isFetchingRef.current = true;

      if (abortControllerRef.current?.signal?.aborted || !abortControllerRef.current) {
        abortControllerRef.current = new AbortController();
      }

      const signal = abortControllerRef.current.signal;

      const { newServices, suggestions } = await getSuggestionsFromSearchValue(input, isSistaminutenSearch, signal);

      isFetchingRef.current = false;

      dispatch({ type: 'ON_SUGGESTION_LOADED', payload: { suggestions, newServices } });

      /**
       * Check if there's a newer input value to process
       */
      if (
        pendingValueRef.current !== null &&
        pendingValueRef?.current !== input &&
        !abortControllerRef.current?.signal?.aborted
      ) {
        fetchSuggestions(pendingValueRef.current);
        pendingValueRef.current = null;
      }
    };

    fetchSuggestions(value);
  };

  const handleOnSuggestionsClearRequested = (props) => {
    abortControllerRef.current?.abort?.();
    dispatch({ type: 'ON_SUGGESTION_LOADED', payload: { suggestions: [], newServices: [] } });
  };

  const handleOnSuggestionSelected = (event, { suggestion, suggestionValue }) => {
    dispatch({ type: 'SET_SHOW_SUGGESTIONS', payload: false });

    if (suggestion.type === 'place') {
      const { placeId, slug } = suggestion;
      setBookingStartingPoint('search_autocomplete');

      let url = `/places/${slug}-${placeId}`;

      if (isSistaminutenSearch) {
        url += '?sistaminuten=1';
      }

      history.push(url);
      return;
    }

    onSelectKeywordSuggestion(suggestionValue?.text?.replace('%', '') ?? suggestion.text ?? '');
  };

  return {
    keyword: state.keyword ?? '',
    suggestions: state.suggestions,
    showSuggestions: state.showSuggestions,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
    onKeyDown: handleOnKeyDown,
    onSuggestionsFetchRequested: handleOnSuggestionsFetchRequested,
    onSuggestionsClearRequested: handleOnSuggestionsClearRequested,
    onSuggestionSelected: handleOnSuggestionSelected,
  };
};

export default useKeywordAutoSuggestManager;
